import { type CSSProperties, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { NODE_HUE } from '@/lib/node-visuals';

// One badge primitive for the whole app — replaces the scattered inline
// `bg-blue-100 text-blue-800`-style chips. A single `tone` resolves to a hue
// (semantic state or a node type), and the soft/outline treatment is derived
// from it with color-mix so every chip sits warm on paper by construction.
export type BadgeTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'customer'
  | 'job'
  | 'challenge'
  | 'opportunity'
  | 'capability'
  | 'assumption'
  | 'idea'
  | 'market_signal';

// Semantic state tones resolve through CSS tokens; node-type tones reference the
// shared NODE_HUE so the palette stays single-sourced (a hue retune tracks the
// canvas, minimap, add-tiles, and these badges together — no duplicated hexes).
const TONE_HUE: Record<BadgeTone, string> = {
  neutral: 'hsl(var(--muted-foreground))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  danger: 'hsl(var(--destructive))',
  info: 'hsl(var(--info))',
  customer: NODE_HUE.customer,
  job: NODE_HUE.job,
  challenge: NODE_HUE.challenge,
  opportunity: NODE_HUE.opportunity,
  capability: NODE_HUE.capability,
  assumption: NODE_HUE.assumption,
  idea: NODE_HUE.idea,
  market_signal: NODE_HUE.market_signal,
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  variant?: 'soft' | 'outline';
}

export function Badge({ tone = 'neutral', variant = 'soft', className, style, ...props }: BadgeProps) {
  // Fall back to neutral for an unknown tone so callers can pass a loose string
  // (e.g. a node type resolved at runtime) without risking a broken color-mix.
  const hue = TONE_HUE[tone] ?? TONE_HUE.neutral;
  const toneStyle: CSSProperties =
    variant === 'outline'
      ? {
          color: `color-mix(in srgb, ${hue} 80%, hsl(var(--foreground)))`,
          borderColor: `color-mix(in srgb, ${hue} 45%, transparent)`,
          background: 'transparent',
        }
      : {
          color: `color-mix(in srgb, ${hue} 78%, hsl(var(--foreground)))`,
          borderColor: `color-mix(in srgb, ${hue} 26%, transparent)`,
          background: `color-mix(in srgb, ${hue} 12%, hsl(var(--card)))`,
        };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none',
        className,
      )}
      style={{ ...toneStyle, ...style }}
      {...props}
    />
  );
}
